import { useState, useMemo } from 'react';
import { useTrialBalance } from '../../hooks/useReports';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { CheckCircle2, AlertTriangle, Search, FileSpreadsheet, Scale, Printer } from 'lucide-react';

export function TrialBalance({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: balances, isLoading, error } = useTrialBalance(startDate, endDate);

  const formatCurrency = (val: number) =>
    `₹${(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalDebit = useMemo(() => (balances || []).reduce((sum, b) => sum + (b.debit || 0), 0), [balances]);
  const totalCredit = useMemo(() => (balances || []).reduce((sum, b) => sum + (b.credit || 0), 0), [balances]);
  const imbalanceDiff = Math.abs(totalDebit - totalCredit);
  const isBalanced = imbalanceDiff < 0.01;

  const filteredBalances = useMemo(() => {
    if (!balances) return [];
    if (!searchQuery.trim()) return balances;
    const q = searchQuery.toLowerCase();
    return balances.filter((b) => b.code.toLowerCase().includes(q) || b.name.toLowerCase().includes(q));
  }, [balances, searchQuery]);

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !balances) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900 text-sm font-medium">
        Failed to generate Trial Balance for the specified period.
      </div>
    );
  }

  const exportCSV = () => {
    const rows = [
      ['Trial Balance Report', `Period: ${startDate} to ${endDate}`],
      [''],
      ['ACCOUNT CODE', 'ACCOUNT NAME', 'DEBIT (INR)', 'CREDIT (INR)'],
      ...balances.map((b) => [b.code, b.name, b.debit || 0, b.credit || 0]),
      ['TOTALS', '', totalDebit, totalCredit],
    ];

    const csvContent = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trial_Balance_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* ── Top Summary Header Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Debit Card */}
        <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 flex items-center justify-center shrink-0">
            <Scale className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Total Debit Balance
            </span>
            <p className="text-lg font-black font-mono text-slate-900 dark:text-slate-100 mt-0.5">
              {formatCurrency(totalDebit)}
            </p>
          </div>
        </div>

        {/* Total Credit Card */}
        <div className="bg-white dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-purple-600/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 flex items-center justify-center shrink-0">
            <Scale className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Total Credit Balance
            </span>
            <p className="text-lg font-black font-mono text-slate-900 dark:text-slate-100 mt-0.5">
              {formatCurrency(totalCredit)}
            </p>
          </div>
        </div>

        {/* Status Verification Card */}
        <div
          className={`p-4 rounded-2xl border shadow-sm flex items-center justify-between gap-3 ${
            isBalanced
              ? 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-900 dark:text-emerald-200'
              : 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/50 text-rose-900 dark:text-rose-200'
          }`}
        >
          <div className="flex items-center gap-3">
            {isBalanced ? (
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400 shrink-0" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-rose-600 dark:text-rose-400 shrink-0" />
            )}
            <div>
              <span className="text-xs font-bold uppercase tracking-wider">Double-Entry Status</span>
              <p className="text-sm font-extrabold mt-0.5">
                {isBalanced ? '✓ Books Balanced Exactly' : `✕ Imbalance: ${formatCurrency(imbalanceDiff)}`}
              </p>
            </div>
          </div>
          <button
            onClick={exportCSV}
            className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-xs font-semibold flex items-center gap-1 shrink-0"
            title="Download CSV"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span className="hidden lg:inline">CSV</span>
          </button>
        </div>
      </div>

      {/* ── Table & Search Controls Container ────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden space-y-0">
        {/* Controls Bar */}
        <div className="p-4 bg-slate-50/70 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search account code or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 font-medium">
            <span>Showing <strong className="text-slate-900 dark:text-slate-100">{filteredBalances.length}</strong> of {balances.length} accounts</span>
            <button
              onClick={() => window.print()}
              className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 transition-colors flex items-center gap-1 text-[11px] font-semibold"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Print</span>
            </button>
          </div>
        </div>

        {/* Table View */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-slate-50/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 font-bold text-xs uppercase tracking-wide">
              <tr>
                <th className="px-6 py-3.5 w-36">Account Code</th>
                <th className="px-6 py-3.5">Account Name</th>
                <th className="px-6 py-3.5 text-right w-44">Debit Balance (Dr)</th>
                <th className="px-6 py-3.5 text-right w-44">Credit Balance (Cr)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-900">
              {filteredBalances.map((b) => (
                <tr key={b.accountId} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40 transition-colors">
                  <td className="px-6 py-3 font-mono text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-800">
                      {b.code}
                    </span>
                  </td>
                  <td className="px-6 py-3 font-bold text-slate-900 dark:text-slate-100 text-xs">
                    {b.name}
                  </td>
                  <td className="px-6 py-3 text-right font-mono text-xs font-semibold text-slate-900 dark:text-slate-100">
                    {b.debit > 0 ? (
                      <span className="text-slate-900 dark:text-slate-100">{formatCurrency(b.debit)}</span>
                    ) : (
                      <span className="text-slate-300 dark:text-slate-700">—</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right font-mono text-xs font-semibold text-slate-900 dark:text-slate-100">
                    {b.credit > 0 ? (
                      <span className="text-slate-900 dark:text-slate-100">{formatCurrency(b.credit)}</span>
                    ) : (
                      <span className="text-slate-300 dark:text-slate-700">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredBalances.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-xs text-slate-400 font-medium">
                    No accounts found matching search filter "{searchQuery}".
                  </td>
                </tr>
              )}
            </tbody>
            {/* Sticky Summary Totals Footer */}
            <tfoot className="bg-slate-50 dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-800 font-black text-xs">
              <tr>
                <td colSpan={2} className="px-6 py-4 text-right uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Total Balances:
                </td>
                <td className="px-6 py-4 text-right font-mono text-sm text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(totalDebit)}
                </td>
                <td className="px-6 py-4 text-right font-mono text-sm text-emerald-700 dark:text-emerald-400">
                  {formatCurrency(totalCredit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
